import { useEffect, useRef, useState, type FormEvent } from "react";
import { CaretDown, MagnifyingGlass, Plus, ShoppingCart, Trash } from "@phosphor-icons/react";
import { formatCny, parseYuanToCents } from "@longjie/shared";
import { ApiError, request } from "./api";
import { AddressModal } from "./DealerApp";
import { ProductCategoryFilter } from "./ProductCategoryFilter";
import { MobileCategoryPicker } from "./MobileCategoryPicker";
import { MobileSkuList } from "./MobileSkuList";
import { usePhoneLayout } from "./usePhoneLayout";
import { useSynchronizedCodeCategories } from "./code-category-sync";
import { CatalogProductRow } from "./CatalogProductRow";
import { DealerCatalogLayout } from "./DealerCatalogLayout";
import { catalogSelectionPath } from "./catalog-selection";
import { ProductSearch } from "./ProductSearch";
import { useProductSearch } from "./useProductSearch";
import { requestDealerCatalog } from "./dealer-catalog-request";
import { Button, EmptyState, Field, LoadError, Modal, Money, StatusBadge } from "./components";
import type { Address, CatalogResponse, Customer, Product, Sku } from "./types";
import "./place-order.css";

type CreatedOrder = { id: string; orderNo: string; totalCents: number };
type Props = {
  notify: (message: string, tone?: "success" | "error" | "info") => void;
  onCreated: (order: CreatedOrder) => void;
  onPendingChange?: (pending: boolean) => void;
};
type Line = { product: Product; sku: Sku; quantity: string; unitPrice: string; priceEdited: boolean };
type CustomerInfo = { name: string; contactName: string; phone: string };
type DeliveryMethod = "SELF_PICKUP" | "DELIVERY";
type Submission = {
  customerInfo: CustomerInfo;
  customer: Customer | null;
  deliveryMethod: DeliveryMethod;
  address: Pick<Address, "contactName" | "phone" | "province" | "city" | "district" | "detail"> | null;
  lines: Line[];
  totalCents: number;
  payload: {
    customerId?: string;
    customerInfo: CustomerInfo;
    deliveryMethod: DeliveryMethod;
    addressId?: string;
    address?: { label: string; contactName: string; phone: string; detail: string };
    note: string;
    idempotencyKey: string;
    items: Array<{ skuId: string; quantity: number; expectedUnitPriceCents: number; unitPriceCents?: number }>;
  };
};
type PriceChange = { skuId: string; newPriceCents: number; oldPriceCents: number; label: string };

const PAGE_SIZE = 30;
const SEARCH_DELAY_MS = 300;
const MAX_LINES = 200;
const MAX_QUANTITY = 999_999;
const MAX_UNIT_PRICE_CENTS = 999_999_999;
function validQuantity(value: string) {
  return /^[1-9]\d*$/.test(value) && Number(value) <= MAX_QUANTITY;
}
function linePriceCents(line: Line) {
  if (!line.priceEdited) return line.sku.priceCents;
  const cents = parseYuanToCents(line.unitPrice);
  return cents !== null && cents <= MAX_UNIT_PRICE_CENTS ? cents : null;
}
function skuProductName(product: Product, sku: Sku) {
  return sku.productName?.trim() || product.name;
}
function addressText(address: Pick<Address, "province" | "city" | "district" | "detail">) {
  return `${address.province}${address.city}${address.district}${address.detail}`;
}
function message(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}
function errorItems(details: unknown): Array<Record<string, unknown>> {
  if (!details || typeof details !== "object") return [];
  const items = (details as { items?: unknown }).items;
  return Array.isArray(items)
    ? items.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object")
    : [];
}
function changeSkuPrice(sku: Sku, priceCents: number): Sku {
  // A changed selling price no longer carries the old promotion label.
  const updated = { ...sku, priceCents };
  delete updated.basePriceCents;
  delete updated.promotion;
  return updated;
}

export function PlaceOrderAdmin({ notify, onCreated, onPendingChange }: Props) {
  const isPhone = usePhoneLayout();
  const [mobileAllProducts, setMobileAllProducts] = useState(false);
  const [customerMode, setCustomerMode] = useState<"temporary" | "existing">("temporary");
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({ name: "", contactName: "", phone: "" });
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>("SELF_PICKUP");
  const [deliveryDetail, setDeliveryDetail] = useState("");
  const [customerInput, setCustomerInput] = useState("");
  const [customerComposing, setCustomerComposing] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerRetry, setCustomerRetry] = useState(0);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState("");
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressId, setAddressId] = useState("");
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesError, setAddressesError] = useState("");
  const [addressRetry, setAddressRetry] = useState(0);
  const [addressOpen, setAddressOpen] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [page, setPage] = useState(1);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryToggleRef = useRef<HTMLButtonElement>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState("");
  const catalogGenerationRef = useRef(0);
  const loadMoreControllerRef = useRef<AbortController | null>(null);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const codeCategories = useSynchronizedCodeCategories(catalog?.codeCategories, 'catalog');
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [catalogRetry, setCatalogRetry] = useState(0);
  const productSearch = useProductSearch({ onCommit: () => {
    setPage(1);
    setCatalogLoading(true);
    setCatalogError("");
    setLoadingMore(false);
    setLoadMoreError("");
  } });
  const { query, revision: searchRevision } = productSearch;
  const [lines, setLines] = useState<Line[]>([]);
  const [note, setNote] = useState("");
  const [confirmation, setConfirmation] = useState<Submission | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submissionUnknown, setSubmissionUnknown] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [priceChanges, setPriceChanges] = useState<PriceChange[]>([]);
  const [unavailableIds, setUnavailableIds] = useState<string[]>([]);
  const submittingRef = useRef(false);
  const customerIdRef = useRef<string | null>(null);
  const attemptRef = useRef<{ signature: string; key: string } | null>(null);

  useEffect(() => {
    if (customerMode !== "existing" || customerComposing || customerInput.trim() === customerQuery) return;
    const timer = window.setTimeout(() => {
      setCustomerQuery(customerInput.trim());
      setCustomersLoading(true);
      setCustomersError("");
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [customerMode, customerInput, customerComposing, customerQuery]);

  useEffect(() => {
    if (customerMode !== "existing") return;
    let current = true;
    const controller = new AbortController();
    const params = new URLSearchParams({ query: customerQuery });
    void request<{ customers: Customer[] }>(`/api/v1/admin/customers?${params}`, { signal: controller.signal })
      .then((result) => { if (current) setCustomers(result.customers.filter((value) => value.active)); })
      .catch((error: unknown) => { if (current) setCustomersError(message(error, "客户加载失败")); })
      .finally(() => { if (current) setCustomersLoading(false); });
    return () => { current = false; controller.abort(); };
  }, [customerMode, customerQuery, customerRetry]);

  useEffect(() => {
    if (!customer) return;
    let current = true;
    const controller = new AbortController();
    void request<{ addresses: Address[] }>(`/api/v1/admin/customers/${customer.id}/addresses`, { signal: controller.signal })
      .then((result) => {
        if (!current || customerIdRef.current !== customer.id) return;
        const available = result.addresses.filter((value) => value.active !== false);
        setAddresses(available);
        setAddressId((selected) => available.some((value) => value.id === selected)
          ? selected : (available.find((value) => value.isDefault) ?? available[0])?.id ?? "");
      })
      .catch((error: unknown) => { if (current) setAddressesError(message(error, "收货地址加载失败")); })
      .finally(() => { if (current) setAddressesLoading(false); });
    return () => { current = false; controller.abort(); };
  }, [customer, addressRetry]);

  useEffect(() => {
    let current = true;
    catalogGenerationRef.current += 1;
    loadMoreControllerRef.current?.abort();
    loadMoreControllerRef.current = null;
    const controller = new AbortController();
    void requestDealerCatalog(catalogSelectionPath("/api/v1/admin/order-catalog", query, categoryId, 1, PAGE_SIZE), controller.signal)
      .then((result) => { if (current) setCatalog(result); })
      .catch((error: unknown) => { if (current) setCatalogError(message(error, "商品目录加载失败")); })
      .finally(() => {
        if (!current) return;
        setCatalogLoading(false);
        setLoadingMore(false);
        setLoadMoreError("");
        setPage(1);
      });
    return () => { current = false; controller.abort(); loadMoreControllerRef.current?.abort(); };
  }, [query, searchRevision, categoryId, catalogRetry]);

  function selectCustomer(value: Customer) {
    if (submittingRef.current || value.id === customer?.id) return;
    customerIdRef.current = value.id;
    setCustomer(value);
    setCustomerInfo({ name: value.name, contactName: value.contactName, phone: value.phone });
    setAddresses([]);
    setAddressId("");
    setAddressesLoading(true);
    setAddressesError("");
    setNote("");
    setAddressOpen(false);
    setConfirmation(null);
    setSubmitError("");
  }

  function changeCustomerMode(mode: "temporary" | "existing") {
    if (mode === customerMode || submittingRef.current) return;
    customerIdRef.current = null;
    setCustomerMode(mode);
    setCustomerComposing(false);
    setCustomersLoading(mode === "existing");
    setCustomersError("");
    setCustomer(null);
    setCustomerInfo({ name: "", contactName: "", phone: "" });
    setAddresses([]);
    setAddressId("");
    setAddressesLoading(false);
    setAddressesError("");
    setDeliveryDetail("");
    setAddressOpen(false);
    setConfirmation(null);
    setNote("");
    setSubmitError("");
  }

  function searchCustomers(event: FormEvent) {
    event.preventDefault();
    if (customerComposing) return;
    setCustomersLoading(true);
    setCustomersError("");
    setCustomerQuery(customerInput.trim());
    setCustomerRetry((value) => value + 1);
  }

  function searchCatalog(event: FormEvent) {
    event.preventDefault();
    productSearch.submit();
  }

  function setCatalogQuantity(product: Product, sku: Sku, quantity: number) {
    if (submittingRef.current || catalogUpdating || catalogError || unavailableIds.includes(sku.id)) return;
    quantity = Math.min(MAX_QUANTITY, Math.max(0, Math.floor(quantity)));
    const existing = lines.find((line) => line.sku.id === sku.id);
    if (quantity > 0 && !existing && lines.length >= MAX_LINES) {
      notify("一张订单最多添加 200 个规格", "info");
      return;
    }
    setLines((current) => quantity === 0 ? current.filter((line) => line.sku.id !== sku.id)
      : current.some((line) => line.sku.id === sku.id)
        ? current.map((line) => line.sku.id === sku.id ? { ...line, quantity: String(quantity) } : line)
        : [...current, { product, sku, quantity: String(quantity), unitPrice: (sku.priceCents / 100).toFixed(2), priceEdited: false }]);
  }

  function selectCatalogCategory(value: string, keepOpen = false) {
    if (submittingRef.current) return;
    if (categoryOpen && !keepOpen) categoryToggleRef.current?.focus();
    if (!keepOpen) setCategoryOpen(false);
    const next = value === "all" ? "" : value;
    if (categoryId === next) return;
    setCategoryId(next);
    setPage(1);
    setCatalogLoading(true);
    setCatalogError("");
    setLoadingMore(false);
    setLoadMoreError("");
  }

  async function loadMoreProducts() {
    if (!catalog?.pagination.hasMore || catalogUpdating || loadMoreControllerRef.current || submittingRef.current) return;
    const generation = catalogGenerationRef.current;
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    setLoadingMore(true);
    setLoadMoreError("");
    const nextPage = page + 1;
    try {
      const result = await requestDealerCatalog(catalogSelectionPath("/api/v1/admin/order-catalog", query, categoryId, nextPage, PAGE_SIZE), controller.signal);
      if (controller.signal.aborted || catalogGenerationRef.current !== generation) return;
      setCatalog((current) => {
        if (!current) return result;
        const merged = new Map(current.products.map((product) => [product.id, product]));
        for (const product of result.products) merged.set(product.id, product);
        return { ...result, products: [...merged.values()] };
      });
      setPage(nextPage);
    } catch (error: unknown) {
      if (!controller.signal.aborted && catalogGenerationRef.current === generation) setLoadMoreError(message(error, "更多商品加载失败"));
    } finally {
      if (loadMoreControllerRef.current === controller) {
        loadMoreControllerRef.current = null;
        setLoadingMore(false);
      }
    }
  }

  const selectedAddress = addresses.find((value) => value.id === addressId);
  const invalidLines = lines.some((line) => !validQuantity(line.quantity) || linePriceCents(line) === null || unavailableIds.includes(line.sku.id));
  const totalCents = lines.reduce((sum, line) => sum + (validQuantity(line.quantity) ? Number(line.quantity) * (linePriceCents(line) ?? 0) : 0), 0);
  const totalReady = Number.isSafeInteger(totalCents) && totalCents <= 2_000_000_000;
  const totalQuantity = lines.reduce((sum, line) => sum + (validQuantity(line.quantity) ? Number(line.quantity) : 0), 0);
  const infoReady = Boolean(customerInfo.name.trim() && (customerMode === "temporary" || customer));
  const deliveryReady = deliveryMethod === "SELF_PICKUP" || (customerMode === "temporary"
    ? Boolean(customerInfo.phone.trim() && deliveryDetail.trim())
    : Boolean(selectedAddress && (selectedAddress.phone.trim() || customerInfo.phone.trim()) && !addressesLoading && !addressesError));
  const canConfirm = Boolean(infoReady && deliveryReady && lines.length && !invalidLines && totalReady);
  const catalogUpdating = catalogLoading || productSearch.pending;
  const customersUpdating = customersLoading || customerComposing || customerInput.trim() !== customerQuery;
  const matchedSkuCount = catalog ? catalog.pagination.totalSkus ?? catalog.products.reduce((sum, product) => sum + product.skus.length, 0) : 0;
  const visibleCategoryId = categoryId;
  const selectedCategory = codeCategories?.find((node) => `code:${node.prefix}` === visibleCategoryId);
  const selectedCategoryLabel = selectedCategory
    ? `${selectedCategory.path} · ${selectedCategory.name ?? `第 ${selectedCategory.depth} 级分类`}`
    : !categoryId ? "全部商品" : categoryId.startsWith("code:")
      ? `${categoryId.slice(5).match(/.{2}/g)?.join(" / ")} · 当前分类`
      : catalog?.categories.find((category) => category.id === categoryId)?.name ?? "当前分类";
  const loadedSkus = catalog?.products.reduce((sum, product) => sum + product.skus.length, 0) ?? 0;

  function confirm() {
    if (!canConfirm) return;
    const snapshot = { name: customerInfo.name.trim(), contactName: customerInfo.contactName.trim() || customerInfo.name.trim(), phone: customerInfo.phone.trim() };
    const address = deliveryMethod === "SELF_PICKUP" ? null : customerMode === "existing" ? selectedAddress ?? null
      : { contactName: snapshot.contactName, phone: snapshot.phone, province: "", city: "", district: "", detail: deliveryDetail.trim() };
    const payload = {
      ...(customer ? { customerId: customer.id } : {}),
      customerInfo: { ...snapshot, contactName: customerInfo.contactName.trim() },
      deliveryMethod,
      ...(deliveryMethod === "DELIVERY" && customer && selectedAddress ? { addressId: selectedAddress.id } : {}),
      ...(deliveryMethod === "DELIVERY" && customerMode === "temporary" && address ? { address: {
        label: "送货地址", contactName: customerInfo.contactName.trim(), phone: address.phone, detail: address.detail,
      } } : {}),
      note: note.trim(),
      items: lines.map((line) => ({ skuId: line.sku.id, quantity: Number(line.quantity), expectedUnitPriceCents: line.sku.priceCents,
        ...(line.priceEdited ? { unitPriceCents: linePriceCents(line)! } : {}) })),
    };
    const signature = JSON.stringify(payload);
    if (attemptRef.current?.signature !== signature) attemptRef.current = { signature, key: crypto.randomUUID() };
    setSubmitError("");
    setConfirmation({ customer, customerInfo: snapshot, deliveryMethod, address, lines, totalCents, payload: { ...payload, idempotencyKey: attemptRef.current.key } });
  }

  async function submit() {
    if (!confirmation || submittingRef.current) return;
    submittingRef.current = true;
    onPendingChange?.(true);
    setSubmitting(true);
    setSubmitError("");
    let resultUnconfirmed = false;
    try {
      const result = await request<{ order: CreatedOrder }>("/api/v1/admin/orders", { method: "POST", body: JSON.stringify(confirmation.payload) });
      setLines([]);
      setNote("");
      setConfirmation(null);
      setSubmissionUnknown(false);
      setPriceChanges([]);
      setUnavailableIds([]);
      attemptRef.current = null;
      onPendingChange?.(false);
      onCreated(result.order);
    } catch (error) {
      if (error instanceof ApiError && error.code === "PRICE_CHANGED") {
        setSubmissionUnknown(false);
        const changes = errorItems(error.details).flatMap((item) => {
          const line = lines.find((value) => value.sku.id === item.skuId);
          return line && !line.priceEdited && typeof item.newPriceCents === "number" && Number.isInteger(item.newPriceCents) && item.newPriceCents >= 0
            ? [{ skuId: line.sku.id, newPriceCents: item.newPriceCents, oldPriceCents: line.sku.priceCents, label: `${skuProductName(line.product, line.sku)} ${line.sku.specification}` }]
            : [];
        });
        const prices = new Map(changes.map((value) => [value.skuId, value.newPriceCents]));
        setLines((current) => current.map((line) => !line.priceEdited && prices.has(line.sku.id)
          ? { ...line, sku: changeSkuPrice(line.sku, prices.get(line.sku.id)!), unitPrice: (prices.get(line.sku.id)! / 100).toFixed(2) } : line));
        setCatalog((current) => current ? { ...current, products: current.products.map((product) => ({ ...product,
          skus: product.skus.map((sku) => prices.has(sku.id) ? changeSkuPrice(sku, prices.get(sku.id)!) : sku) })) } : null);
        setPriceChanges(changes);
        setConfirmation(null);
        setSubmitError(changes.length ? "商品价格已更新，明细已保留，请重新核对金额并确认。" : "商品价格已变化，请刷新商品目录后重新核对。" );
        notify("商品价格已变化，请重新确认", "info");
      } else if (error instanceof ApiError && error.code === "SKU_UNAVAILABLE") {
        setSubmissionUnknown(false);
        setUnavailableIds(errorItems(error.details).map((item) => item.skuId).filter((value): value is string => typeof value === "string"));
        setConfirmation(null);
        setSubmitError("部分规格已下架，请移除标记的规格后重新确认。");
      } else {
        // Authentication/rate-limit rejection of a retry does not resolve an earlier uncertain result.
        resultUnconfirmed = submissionUnknown || !(error instanceof ApiError) || error.status >= 500;
        setSubmissionUnknown(resultUnconfirmed);
        setSubmitError(message(error, "订单提交失败，请重试。重试会核对同一次提交，避免重复下单。"));
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      onPendingChange?.(resultUnconfirmed);
    }
  }

  return <div className="place-order">
    <div className="page-heading"><div><h2>填写客户信息，直接开单</h2><p>临时客户无需建账号，登记客户信息即可开单。</p></div></div>
    <a className="place-order__jump" href="#place-order-draft-title"><span><ShoppingCart aria-hidden="true" />查看订单明细（{lines.length}）</span><Money cents={totalCents} /></a>
    <div className="place-order__layout">
      <div className="place-order__main">
        <section className="place-order__panel place-order__customer-panel" aria-labelledby="place-order-customer-title">
          <header className="place-order__section-heading"><span className="place-order__step">1</span><h2 id="place-order-customer-title">客户与交付信息</h2></header>
          <div className="place-order__choices" aria-label="客户类型"><Button variant={customerMode === "temporary" ? "primary" : "secondary"} aria-pressed={customerMode === "temporary"} disabled={submitting} onClick={() => changeCustomerMode("temporary")}>临时客户</Button><Button variant={customerMode === "existing" ? "primary" : "secondary"} aria-pressed={customerMode === "existing"} disabled={submitting} onClick={() => changeCustomerMode("existing")}>已有客户</Button></div>
          {customerMode === "existing" && <><form className="place-order__search place-order__filter-search" onSubmit={searchCustomers}><Field label="搜索客户"><input value={customerInput} maxLength={100} onChange={(event) => setCustomerInput(event.target.value)} onCompositionStart={() => setCustomerComposing(true)} onCompositionEnd={() => setCustomerComposing(false)} placeholder="输入客户编码、名称、账号、联系人或电话" disabled={submitting} /></Field><Button type="submit" disabled={submitting || customersLoading}><MagnifyingGlass />搜索</Button></form>
          <div className="place-order__search-status" role="status">{customersUpdating ? "正在筛选客户…" : customersError ? "搜索失败，请重试；下方保留上次结果。" : `找到 ${customers.length} 个客户`}</div>
          {customersError && <LoadError title="客户加载失败" description={customersError} onRetry={() => { setCustomersLoading(true); setCustomersError(""); setCustomerRetry((value) => value + 1); }} />}
          <div className="place-order__customers" role="region" aria-label="客户搜索结果" aria-busy={customersUpdating}>{customers.length ? customers.map((value) => <button key={value.id} type="button" aria-label={`选择客户 ${value.name}`} aria-pressed={value.id === customer?.id} disabled={submitting || customersUpdating || Boolean(customersError)} onClick={() => selectCustomer(value)}><strong>{value.name}</strong><small>{value.code} · {value.contactName} {value.phone}</small>{value.id === customer?.id && <StatusBadge tone="info">已选择</StatusBadge>}</button>) : !customersUpdating && !customersError && <p className="place-order__muted">没有找到启用的客户，请调整搜索条件或先在客户账号中新增客户。</p>}</div>
          {customer && <div className="place-order__selected-customer"><strong>关联客户：{customer.name}</strong><small>客户编码 {customer.code} · 订单记入该客户台账</small></div>}
          </>}
          <div className="place-order__customer-form">
            <Field label="客户名称 / 单位（必填）"><input value={customerInfo.name} maxLength={200} required disabled={submitting || (customerMode === "existing" && !customer)} onChange={(event) => setCustomerInfo((current) => ({ ...current, name: event.target.value }))} placeholder="例如：王师傅、某某装修公司" /></Field>
            <div className="place-order__contact-row" role="group" aria-label="联系信息">
              <Field label="联系人" hint="选填，未填写时使用客户名称"><input value={customerInfo.contactName} maxLength={80} disabled={submitting || (customerMode === "existing" && !customer)} onChange={(event) => setCustomerInfo((current) => ({ ...current, contactName: event.target.value }))} /></Field>
              <Field label="联系电话" hint={deliveryMethod === "SELF_PICKUP" ? "到店自提可不填写" : customerMode === "temporary" ? "送货必填" : "送货使用所选地址的电话，或填写客户电话"}><input type="tel" value={customerInfo.phone} maxLength={30} disabled={submitting || (customerMode === "existing" && !customer)} onChange={(event) => setCustomerInfo((current) => ({ ...current, phone: event.target.value }))} /></Field>
            </div>
          </div>
          <div className="place-order__delivery"><h3>交付方式</h3><div className="place-order__choices" aria-label="交付方式"><Button variant={deliveryMethod === "SELF_PICKUP" ? "primary" : "secondary"} aria-pressed={deliveryMethod === "SELF_PICKUP"} disabled={submitting} onClick={() => { setDeliveryMethod("SELF_PICKUP"); setConfirmation(null); }}>到店自提</Button><Button variant={deliveryMethod === "DELIVERY" ? "primary" : "secondary"} aria-pressed={deliveryMethod === "DELIVERY"} disabled={submitting} onClick={() => { setDeliveryMethod("DELIVERY"); setConfirmation(null); }}>送货</Button></div>
          {deliveryMethod === "SELF_PICKUP" ? <p className="place-order__muted">客户到店拿货，无需填写收货地址。</p>
            : customerMode === "temporary" ? <Field label="送货详细地址" hint="必填，填写省市区、街道、门牌号或具体位置"><textarea rows={3} value={deliveryDetail} maxLength={300} disabled={submitting} onChange={(event) => setDeliveryDetail(event.target.value)} /></Field>
            : customer && <>
            <div className="place-order__address-heading"><h3>收货地址</h3><Button variant="secondary" onClick={() => setAddressOpen(true)} disabled={submitting || addressesLoading}><Plus />新增地址</Button></div>
            {addressesError ? <LoadError title="收货地址加载失败" description={addressesError} onRetry={() => { setAddressesLoading(true); setAddressesError(""); setAddressRetry((value) => value + 1); }} />
              : addressesLoading ? <p role="status">正在加载收货地址…</p>
                : addresses.length ? <><Field label="选择收货地址"><select value={addressId} onChange={(event) => setAddressId(event.target.value)} disabled={submitting}>{addresses.map((address) => <option key={address.id} value={address.id}>{address.label} · {address.contactName}{address.isDefault ? "（默认）" : ""}</option>)}</select></Field>{selectedAddress && <div className="place-order__address"><strong>{selectedAddress.contactName} {selectedAddress.phone}</strong><p>{addressText(selectedAddress)}</p></div>}</>
                  : <p className="place-order__muted">该客户暂无可用收货地址，请新增地址后下单。</p>}
          </>}
          </div>
          <div className="place-order__note"><Field label="订单备注" hint="整单共用一条备注，选填，最多 500 字"><textarea rows={2} maxLength={500} value={note} disabled={submitting} placeholder="例如：送货时间、包装要求" onChange={(event) => setNote(event.target.value)} /></Field></div>
        </section>
        <section className="place-order__panel place-order__catalog-panel" aria-labelledby="place-order-catalog-title">
          <header className="place-order__section-heading"><span className="place-order__step">2</span><h2 id="place-order-catalog-title">选择商品规格</h2></header>
          <p className="place-order__muted">选择分类后，直接在规格旁填写数量或点加减号。搜索支持商品名称、编码和规格。</p>
          <form className="place-order__search place-order__filter-search" onSubmit={searchCatalog}><ProductSearch search={productSearch} disabled={submitting} busy={catalogLoading} /></form>
          <DealerCatalogLayout enabled sidebarId="place-order-category-panel" contentId="place-order-product-panel" storageKey="longjie-admin-order-category-width" defaultWidth={230}>
            <aside id="place-order-category-panel" className="category-navigation dealer-category-navigation place-order__category-navigation" aria-label="订购商品分类">
              {isPhone ? <MobileCategoryPicker nodes={codeCategories ?? []} legacyCategories={catalog?.categories ?? []} showLegacyCategories
                selectedValue={visibleCategoryId || "all"} compact={Boolean(query) || mobileAllProducts} showAllProducts={mobileAllProducts && !query}
                onShowAllProducts={() => { productSearch.reset(""); setMobileAllProducts(true); selectCatalogCategory(""); }}
                onSelect={(value) => { productSearch.reset(""); setMobileAllProducts(false); selectCatalogCategory(value); }} /> : <><h3>商品分类</h3>
              <button ref={categoryToggleRef} type="button" className="dealer-category-toggle" aria-label={`商品分类：${selectedCategoryLabel}`} aria-expanded={categoryOpen} aria-controls="place-order-category-options" disabled={submitting} onClick={() => setCategoryOpen((open) => !open)}><span><strong>商品分类</strong><small>{selectedCategoryLabel}</small></span><CaretDown aria-hidden="true" /></button>
              <div id="place-order-category-options" className={categoryOpen ? "dealer-category-options is-open" : "dealer-category-options"}>
                <ProductCategoryFilter nodes={codeCategories ?? []} legacyCategories={catalog?.categories ?? []}
                  value={visibleCategoryId} onChange={selectCatalogCategory} disabled={submitting} />
              </div></>}
            </aside>
            <div id="place-order-product-panel" className="place-order__catalog-content" hidden={isPhone && !query && !visibleCategoryId && !mobileAllProducts && !catalogError}>
              {!isPhone && <div className="dealer-selected-category"><h3>{selectedCategoryLabel}</h3>{visibleCategoryId && <button type="button" disabled={submitting} onClick={() => selectCatalogCategory("")}>清除分类筛选</button>}{selectedCategory && <p>包含该分类及全部下级商品，可直接选择规格和数量。</p>}</div>}
              <div className="place-order__search-status" role="status">{catalogUpdating ? "正在搜索…" : catalogError ? "搜索失败，请重试；下方保留上次结果。" : catalog ? query ? `找到 ${matchedSkuCount} 个规格，已显示 ${loadedSkus} 个` : `找到 ${catalog.pagination.totalProducts} 个商品，已显示 ${catalog.products.length} 个` : ""}</div>
              <div className="place-order__results" role="region" aria-label="商品搜索结果" aria-busy={catalogUpdating || loadingMore}>
                {catalogError && <LoadError title="商品目录加载失败" description={catalogError} onRetry={() => { setCatalogLoading(true); setCatalogError(""); setCatalogRetry((value) => value + 1); }} />}
                {catalog ? catalog.products.length ? isPhone ? <MobileSkuList products={catalog.products} canOrder
                  quantityForSku={(sku) => { const line = lines.find((value) => value.sku.id === sku.id); return line && validQuantity(line.quantity) ? Number(line.quantity) : 0; }}
                  onQuantity={setCatalogQuantity} disabled={(sku) => submitting || catalogUpdating || Boolean(catalogError) || unavailableIds.includes(sku.id)}
                  renderPrice={(sku) => { const line = lines.find((value) => value.sku.id === sku.id); const cents = line ? linePriceCents(line) : sku.priceCents;
                    return <span className="staff-sku-price"><span>单价 {cents === null ? "—" : <Money cents={cents} />} / {sku.unit}</span>
                      <small className={sku.stockQuantity !== undefined && sku.stockQuantity < 0 ? "stock-negative" : undefined}>库存 {sku.stockQuantity === undefined ? "未提供" : `${sku.stockQuantity.toLocaleString("zh-CN")} ${sku.unit}`}</small></span>; }}
                  renderSubtotal={(sku, quantity) => { const line = lines.find((value) => value.sku.id === sku.id); const cents = line ? linePriceCents(line) : sku.priceCents;
                    return cents === null || (line && !validQuantity(line.quantity)) ? "—" : <Money cents={cents * quantity} />; }} />
                  : <div className="product-list place-order__catalog">{catalog.products.map((product) => <CatalogProductRow key={product.id} product={product} canOrder quantityForSku={(sku) => { const line = lines.find((value) => value.sku.id === sku.id); return line && validQuantity(line.quantity) ? Number(line.quantity) : 0; }} onQuantity={setCatalogQuantity}
                  metricHeading="库存" renderMetric={(sku) => <span className={`place-order__stock${sku.stockQuantity !== undefined && sku.stockQuantity < 0 ? " stock-negative" : ""}`} aria-label={`库存 ${sku.stockQuantity === undefined ? "未提供" : `${sku.stockQuantity} ${sku.unit}`}`}>{sku.stockQuantity === undefined ? "—" : `${sku.stockQuantity.toLocaleString("zh-CN")} ${sku.unit}`}</span>}
                  renderTotal={(sku, quantity) => { const line = lines.find((value) => value.sku.id === sku.id); const cents = line ? linePriceCents(line) : sku.priceCents; return cents === null || (line && !validQuantity(line.quantity)) ? <span>—</span> : <Money cents={cents * quantity} className={quantity === 0 ? "muted" : ""} />; }}
                  quantityLabelForSku={(sku) => `${skuProductName(product, sku)} ${sku.specification} ${sku.code} 订购数量，单位${sku.unit}`} disabled={(sku) => submitting || catalogUpdating || Boolean(catalogError) || unavailableIds.includes(sku.id)} />)}</div>
                  : <EmptyState title="暂无可下单商品" description="请调整搜索条件或分类，确认商品及规格已启用。" /> : null}
              </div>
              {loadMoreError && <div className="inline-error" role="alert">{loadMoreError}</div>}
              {catalog?.pagination.hasMore && <div className="product-list__footer"><Button variant="secondary" loading={loadingMore} disabled={catalogUpdating || submitting || Boolean(catalogError)} onClick={() => void loadMoreProducts()}>{loadMoreError ? "重新加载更多" : `继续加载（剩余 ${catalog.pagination.totalProducts - catalog.products.length} 个）`}</Button></div>}
            </div>
          </DealerCatalogLayout>
        </section>
      </div>
      <section className="place-order__panel place-order__draft" aria-labelledby="place-order-draft-title">
        <header className="place-order__section-heading"><span className="place-order__step">3</span><h2 id="place-order-draft-title">订单明细</h2><ShoppingCart aria-hidden="true" /></header>
        {submitError && !confirmation && <div className="inline-error" role="alert">{submitError}{priceChanges.length > 0 && <ul>{priceChanges.map((change) => <li key={change.skuId}>{change.label}：{formatCny(change.oldPriceCents)} → {formatCny(change.newPriceCents)}</li>)}</ul>}</div>}
        {lines.length ? <div className="place-order__lines">{lines.map((line) => <article key={line.sku.id} className="place-order__line"><div className="place-order__line-heading"><div><strong>{skuProductName(line.product, line.sku)}</strong><small>{isPhone && "规格："}{line.sku.specification} · {line.sku.code}</small></div><button type="button" className="icon-button" aria-label={`移除 ${skuProductName(line.product, line.sku)} ${line.sku.specification}`} onClick={() => setLines((current) => current.filter((value) => value.sku.id !== line.sku.id))} disabled={submitting}><Trash /></button></div><div className="place-order__line-controls"><Field label={`数量（${line.sku.unit}）`} error={!validQuantity(line.quantity) ? "请输入 1 至 999999 的整数" : unavailableIds.includes(line.sku.id) ? "规格已下架，请移除" : ""}><input type="number" aria-label={`${skuProductName(line.product, line.sku)} ${line.sku.specification} 数量`} inputMode="numeric" min={1} max={MAX_QUANTITY} step={1} value={line.quantity} disabled={submitting} onChange={(event) => { const quantity = event.target.value; setLines((current) => current.map((value) => value.sku.id === line.sku.id ? { ...value, quantity } : value)); }} /></Field><Field label="单价（元）" error={linePriceCents(line) === null ? "请输入 0 至 9999999.99，最多两位小数" : ""}><input type="text" aria-label={`${skuProductName(line.product, line.sku)} ${line.sku.specification} 单价`} inputMode="decimal" maxLength={16} value={line.unitPrice ?? (line.sku.priceCents / 100).toFixed(2)} disabled={submitting} onChange={(event) => { const unitPrice = event.target.value; setLines((current) => current.map((value) => value.sku.id === line.sku.id ? { ...value, unitPrice, priceEdited: true } : value)); }} /></Field><Field label="金额（元）"><output className="place-order__line-amount" aria-label={`${skuProductName(line.product, line.sku)} ${line.sku.specification} 金额`}>{validQuantity(line.quantity) && linePriceCents(line) !== null ? formatCny(linePriceCents(line)! * Number(line.quantity)) : "—"}</output></Field></div></article>)}</div>
          : <EmptyState title="尚未添加商品" description="从商品目录中选择规格，加入订单明细。" />}
        <footer className="place-order__summary"><p><span>{lines.length} 个规格 · 共 {totalQuantity} 件</span><strong>合计 <Money cents={totalCents} /></strong></p><Button disabled={!canConfirm || submitting} onClick={confirm}>核对并下单</Button>{!infoReady ? <small>{customerMode === "existing" && !customer ? "请先选择客户" : "请填写客户名称 / 单位"}</small> : !deliveryReady ? <small>{customerMode === "temporary" ? "请填写送货电话及详细地址" : "请先选择可用收货地址及电话"}</small> : invalidLines ? <small>请修正数量、单价或移除下架规格</small> : !totalReady ? <small>订单金额过大，请减少数量或拆分订单</small> : !lines.length ? <small>请至少添加一个商品规格</small> : null}</footer>
      </section>
    </div>
    {addressOpen && customer && <AddressModal key={customer.id} customerId={customer.id} onClose={() => setAddressOpen(false)} onCreated={(address) => {
      if (customerIdRef.current !== customer.id) return;
      setAddresses((current) => [...current.map((value) => address.isDefault ? { ...value, isDefault: false } : value), address]);
      setAddressId(address.id);
      setAddressesError("");
      setAddressOpen(false);
      notify("收货地址已添加");
    }} />}
    {confirmation && <Modal title="确认代客下单" description="请核对客户、收货信息及商品明细。确认后创建正式订单。" onClose={() => { if (!submittingRef.current && !submissionUnknown) setConfirmation(null); }} footer={<><Button variant="secondary" disabled={submitting || submissionUnknown} onClick={() => setConfirmation(null)}>返回修改</Button><Button loading={submitting} onClick={() => void submit()}>确认提交订单</Button></>}>
      {submitError && <div className="inline-error" role="alert">{submitError}<p>{submissionUnknown ? "此次提交结果尚未确认，当前不能修改或离开页面，请先直接重试核对订单，再开始新的订单。" : "可直接重试此次提交，系统会核对是否已创建订单。"}</p></div>}
      <div className="place-order__review"><div><small>下单客户</small><strong>{confirmation.customerInfo.name}{confirmation.customer ? `（${confirmation.customer.code}）` : "（临时客户）"}</strong><p>联系人：{confirmation.customerInfo.contactName}</p><p>联系电话：{confirmation.customerInfo.phone || "未填写"}</p></div><div><small>交付方式</small><strong>{confirmation.deliveryMethod === "SELF_PICKUP" ? "到店自提" : "送货"}</strong></div>{confirmation.address && <div><small>收货信息</small><strong>{confirmation.address.contactName} {confirmation.address.phone}</strong><p>{addressText(confirmation.address)}</p></div>}<ul>{confirmation.lines.map((line) => <li key={line.sku.id}><div><strong>{skuProductName(line.product, line.sku)}</strong><small>{isPhone && "规格："}{line.sku.specification} · {formatCny(linePriceCents(line)!)} / {line.sku.unit} × {line.quantity}</small></div><Money cents={linePriceCents(line)! * Number(line.quantity)} /></li>)}</ul>{confirmation.payload.note && <div><small>订单备注</small><p>{confirmation.payload.note}</p></div>}<p className="place-order__review-total"><span>订单合计</span><Money cents={confirmation.totalCents} /></p></div>
    </Modal>}
  </div>;
}
